import React, { useCallback, useEffect, useRef, useState } from "react";
import DiyLayout from "../../components/Layout/DiyLayout";
import { useDiySelfClient } from "../../hooks/useDiySelfClient";
import { aiApi, type ChatHistoryMessage } from "../../api/aiApi";
import Spinner from "../../components/ui/Spinner";
import { ArrowUp, MessageCircle } from "lucide-react";
import "../../styles/diy-animations.css";

interface Msg {
  role: "user" | "assistant";
  text: string;
}

const SUGGESTED = [
  "How do I read my credit report?",
  "What should I dispute first?",
  "How long until I see results?",
  "Explain my rights under FCRA",
];

const DiyAiCoachPage: React.FC = () => {
  const { clientId, client, loading: clientLoading } = useDiySelfClient();
  const [messages, setMessages] = useState<Msg[]>([]);
  const [input, setInput] = useState("");
  const [sending, setSending] = useState(false);
  const bottomRef = useRef<HTMLDivElement>(null);
  const messagesRef = useRef(messages);
  messagesRef.current = messages;

  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages]);

  const send = useCallback(
    async (text: string) => {
      const trimmed = text.trim();
      if (!trimmed || sending) {
        return;
      }
      setSending(true);
      setInput("");
      const userMsg: Msg = { role: "user", text: trimmed };
      const snapshot = [...messagesRef.current, userMsg];
      messagesRef.current = snapshot;
      setMessages(snapshot);
      const history: ChatHistoryMessage[] = snapshot.slice(-12).map((x) => ({
        role: x.role,
        content: x.text,
      }));
      try {
        const res = await aiApi.chat({
          clientId: clientId ?? undefined,
          message: trimmed,
          history,
        });
        const withBot = [...messagesRef.current, { role: "assistant" as const, text: res.response || "No response." }];
        messagesRef.current = withBot;
        setMessages(withBot);
      } catch {
        const errSnap = [
          ...messagesRef.current,
          {
            role: "assistant" as const,
            text: "Sorry — I couldn't reach the AI service. Try again shortly.",
          },
        ];
        messagesRef.current = errSnap;
        setMessages(errSnap);
      } finally {
        setSending(false);
      }
    },
    [clientId, sending]
  );

  const showWelcome = messages.length === 0 && !sending;
  const last = messages[messages.length - 1];
  const showChipsAfterAi = !showWelcome && last?.role === "assistant" && !sending;

  return (
    <DiyLayout>
      <div
        className="mx-auto flex max-w-lg flex-col md:max-w-2xl"
        style={{ minHeight: "calc(100vh - 11rem)", fontFamily: "'DM Sans', sans-serif" }}
      >
        <div className="shrink-0 border-b pb-4" style={{ borderColor: "rgba(255,255,255,0.06)" }}>
          <div className="flex items-center gap-3">
            <div
              className="flex h-10 w-10 items-center justify-center rounded-xl"
              style={{ background: "linear-gradient(135deg, #8B5CF6, #6D28D9)" }}
            >
              <MessageCircle className="h-5 w-5 text-white" strokeWidth={2} />
            </div>
            <div>
              <h1 style={{ fontFamily: "'Sora', sans-serif", fontWeight: 600, fontSize: 16, color: "#F1F5F9" }}>
                AI Credit Coach
              </h1>
              <p className="text-[11px]" style={{ color: "#64748B" }}>
                Powered by IntelliWarrior AI
                {client ? ` · ${client.firstName}` : null}
              </p>
            </div>
          </div>
        </div>

        <div className="min-h-0 flex-1 overflow-y-auto py-4">
          {showWelcome ? (
            <div className="flex h-full flex-col items-center justify-center px-4 py-8 text-center">
              <div
                className="animate-float mb-4 flex h-12 w-12 items-center justify-center rounded-xl"
                style={{ background: "linear-gradient(135deg, #8B5CF6, #6D28D9)" }}
              >
                <MessageCircle className="h-6 w-6 text-white" strokeWidth={2} />
              </div>
              <p style={{ fontFamily: "'Sora', sans-serif", fontWeight: 600, fontSize: 18, color: "#F1F5F9" }}>
                Hi! I&apos;m your AI Credit Coach
              </p>
              <p className="mt-1" style={{ fontSize: 14, color: "#94A3B8" }}>
                Ask me anything about credit repair
              </p>
              <div className="mt-6 flex max-w-full flex-wrap justify-center gap-2">
                {SUGGESTED.map((q) => (
                  <button
                    key={q}
                    type="button"
                    disabled={clientLoading}
                    onClick={() => void send(q)}
                    className="cursor-pointer rounded-full border px-4 py-1.5 text-xs font-medium transition-colors duration-200 disabled:opacity-40"
                    style={{
                      background: "rgba(139,92,246,0.08)",
                      borderColor: "rgba(139,92,246,0.2)",
                      color: "#8B5CF6",
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.background = "#8B5CF6";
                      e.currentTarget.style.color = "white";
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.background = "rgba(139,92,246,0.08)";
                      e.currentTarget.style.color = "#8B5CF6";
                    }}
                  >
                    {q}
                  </button>
                ))}
              </div>
            </div>
          ) : (
            <div className="space-y-4 px-1">
              {messages.map((msg, i) => (
                <div key={i} className={`flex ${msg.role === "user" ? "justify-end" : "justify-start"}`}>
                  <div className="max-w-[90%]">
                    {msg.role === "assistant" ? (
                      <p className="mb-1 flex items-center gap-1.5 text-[10px] font-medium" style={{ color: "#8B5CF6" }}>
                        <span className="h-1.5 w-1.5 rounded-full" style={{ background: "#8B5CF6" }} />
                        AI Coach
                      </p>
                    ) : null}
                    <div
                      className="rounded-2xl px-[18px] py-[14px] text-sm leading-relaxed"
                      style={
                        msg.role === "user"
                          ? {
                              background: "linear-gradient(135deg, #10B981, #0EA5E9)",
                              color: "white",
                              borderRadius: "16px",
                              borderBottomRightRadius: "4px",
                              fontFamily: "'DM Sans', sans-serif",
                            }
                          : {
                              background: "rgba(255,255,255,0.04)",
                              border: "1px solid rgba(255,255,255,0.07)",
                              color: "#E2E8F0",
                              borderRadius: "16px",
                              borderBottomLeftRadius: "4px",
                              fontFamily: "'DM Sans', sans-serif",
                            }
                      }
                    >
                      {msg.text}
                    </div>
                  </div>
                </div>
              ))}
              {sending ? (
                <div className="flex justify-start">
                  <div
                    className="flex items-center gap-2 rounded-2xl px-4 py-3 text-sm"
                    style={{
                      background: "rgba(255,255,255,0.04)",
                      border: "1px solid rgba(255,255,255,0.07)",
                      color: "#94A3B8",
                    }}
                  >
                    <Spinner className="h-4 w-4" />
                    Thinking…
                  </div>
                </div>
              ) : null}
              {showChipsAfterAi ? (
                <div className="flex gap-2 overflow-x-auto pb-1 pt-1">
                  {SUGGESTED.map((q) => (
                    <button
                      key={q}
                      type="button"
                      onClick={() => void send(q)}
                      className="shrink-0 cursor-pointer rounded-full border px-4 py-1.5 text-xs font-medium transition-colors duration-200"
                      style={{
                        background: "rgba(139,92,246,0.08)",
                        borderColor: "rgba(139,92,246,0.2)",
                        color: "#8B5CF6",
                      }}
                      onMouseEnter={(e) => {
                        e.currentTarget.style.background = "#8B5CF6";
                        e.currentTarget.style.color = "white";
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.background = "rgba(139,92,246,0.08)";
                        e.currentTarget.style.color = "#8B5CF6";
                      }}
                    >
                      {q}
                    </button>
                  ))}
                </div>
              ) : null}
              <div ref={bottomRef} />
            </div>
          )}
        </div>

        <form
          className="mt-auto shrink-0 border-t py-3"
          style={{
            background: "rgba(11,17,32,0.95)",
            borderColor: "rgba(255,255,255,0.06)",
            backdropFilter: "blur(12px)",
          }}
          onSubmit={(e) => {
            e.preventDefault();
            void send(input);
          }}
        >
          <div className="flex items-center gap-2">
            <input
              value={input}
              onChange={(e) => setInput(e.target.value)}
              placeholder="Ask about disputes, scores, timelines…"
              className="min-w-0 flex-1 rounded-xl border px-4 py-3 text-sm outline-none transition-colors"
              style={{
                background: "rgba(255,255,255,0.04)",
                borderColor: "rgba(255,255,255,0.08)",
                color: "white",
                fontFamily: "'DM Sans', sans-serif",
              }}
              disabled={sending}
              onFocus={(e) => {
                e.currentTarget.style.borderColor = "#8B5CF6";
              }}
              onBlur={(e) => {
                e.currentTarget.style.borderColor = "rgba(255,255,255,0.08)";
              }}
            />
            <button
              type="submit"
              disabled={sending || !input.trim()}
              className="flex h-11 w-11 shrink-0 cursor-pointer items-center justify-center rounded-xl border-0 text-white disabled:opacity-40"
              style={{
                background: "linear-gradient(135deg, #8B5CF6, #6D28D9)",
                boxShadow: "0 4px 12px rgba(139,92,246,0.3)",
              }}
              aria-label="Send"
            >
              <ArrowUp className="h-5 w-5" strokeWidth={2.5} />
            </button>
          </div>
        </form>
      </div>
    </DiyLayout>
  );
};

export default DiyAiCoachPage;
